/**
 * 정산 수수료 상세 화면.
 * admin-ruda-web `settlementFeeDetails.js` 포팅.
 */
import {
  useCommissionDetail,
  useUpdateCommissionDetail,
} from '@/entities/commission/hooks/useCommissions';
import {
  formatCommissionRateInput,
  toCommissionRateDecimal,
} from '@/pages/settlement-fees/commissionDetailUtils';
import { useMenuAccess } from '@/layouts/MenuAccessContext';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { DatePicker } from '@/shared/components/DatePicker';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { formatYearMonth } from '@/shared/lib/dateFormat';
import { useParams, useSearch } from '@tanstack/react-router';
import { Save } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { ToolbarRow } from '@/shared/components/ui/row';

export const SettlementFeeDetailPage: React.FC = () => {
  const toast = useToast();
  const { readOnly } = useMenuAccess();
  const { drugCode: drugCodeParam } = useParams({ strict: false }) as {
    drugCode?: string;
  };
  const search = useSearch({ strict: false }) as { refYm?: string };
  const refYm = search.refYm ?? '';
  const drugCode = drugCodeParam ? decodeURIComponent(drugCodeParam) : '';

  const { data, isLoading, isError, error, refetch } = useCommissionDetail(
    drugCode && refYm ? { refYm, drugCode } : null,
  );
  const updateDetail = useUpdateCommissionDetail();

  const [form, setForm] = useState({
    price: '',
    standardCommissionRate: '',
    standardCommissionNote: '',
    incentive: '',
    incentiveNote: '',
    outOfStock: false,
    outOfStockFrom: '',
    outOfStockTo: '',
  });
  const [isChanged, setIsChanged] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);

  useEffect(() => {
    if (isError && error) handleApiError(error, toast);
  }, [isError, error, toast]);

  useEffect(() => {
    if (!data) return;
    setForm({
      price: String(data.price ?? ''),
      standardCommissionRate: formatCommissionRateInput(data.standardCommissionRate),
      standardCommissionNote: data.standardCommissionNote ?? '',
      incentive: data.incentive != null ? String(data.incentive) : '',
      incentiveNote: data.incentiveNote ?? '',
      outOfStock: Boolean(data.outOfStock),
      outOfStockFrom: data.outOfStockFrom?.slice(0, 10) ?? '',
      outOfStockTo: data.outOfStockTo?.slice(0, 10) ?? '',
    });
    setIsChanged(false);
  }, [data]);

  const updateField = <K extends keyof typeof form>(
    key: K,
    value: (typeof form)[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setIsChanged(true);
  };

  const handleSave = async () => {
    if (!refYm || !drugCode) return;
    try {
      const rate = toCommissionRateDecimal(form.standardCommissionRate);
      await updateDetail.mutateAsync({
        refYm,
        drugCode,
        payload: {
          price: Number(form.price) || 0,
          standardCommissionRate: rate,
          standardCommissionNote: form.standardCommissionNote || undefined,
          incentive: form.incentive || undefined,
          incentiveNote: form.incentiveNote || undefined,
          outOfStock: form.outOfStock,
          outOfStockFrom: form.outOfStockFrom || undefined,
          outOfStockTo: form.outOfStockTo || undefined,
        },
      });
      toast.success('정산 수수료 정보를 저장했습니다.');
      setIsChanged(false);
      await refetch();
    } catch (e) {
      if (e instanceof Error && e.message === 'standard_commission_rate_error') {
        toast.error('기본 수수료율 형식이 올바르지 않습니다.');
        return;
      }
      handleApiError(e, toast);
    } finally {
      setSaveOpen(false);
    }
  };

  if (!drugCode || !refYm) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        잘못된 경로입니다. 목록에서 다시 선택해 주세요.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="py-16">
        <LoadingSpinner tip="정산 수수료 정보를 불러오는 중..." />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        정산 수수료 정보를 찾을 수 없습니다.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ToolbarRow>
        <h2 className="text-2xl font-semibold">정산 수수료 상세</h2>
        <MenuWriteGate>
          <Button
            className="ml-auto"
            disabled={!isChanged || updateDetail.isPending}
            onClick={() => setSaveOpen(true)}
          >
            <Save className="mr-2 h-4 w-4" />
            저장
          </Button>
        </MenuWriteGate>
      </ToolbarRow>

      <Card>
        <CardHeader>
          <CardTitle>{data.drugName}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-1">
              <Label>기준년월</Label>
              <Input readOnly value={formatYearMonth(data.referenceYearMonth)} />
            </div>
            <div className="space-y-1">
              <Label>제약사명</Label>
              <Input readOnly value={data.pharmaCompanyName} />
            </div>
            <div className="space-y-1">
              <Label>의약품 코드</Label>
              <Input readOnly value={data.drugCode} />
            </div>
            <div className="space-y-1">
              <Label>의약품 가격</Label>
              <Input
                type="number"
                value={form.price}
                disabled={readOnly}
                onChange={(e) => updateField('price', e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label>기준 수수료율 (%)</Label>
              <Input
                value={form.standardCommissionRate}
                disabled={readOnly}
                onChange={(e) =>
                  updateField('standardCommissionRate', e.target.value)
                }
                placeholder="예: 12.5"
              />
            </div>
            <div className="space-y-1">
              <Label>인센티브</Label>
              <Input
                value={form.incentive}
                disabled={readOnly}
                onChange={(e) => updateField('incentive', e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <input
                id="outOfStock"
                type="checkbox"
                checked={form.outOfStock}
                disabled={readOnly}
                onChange={(e) => updateField('outOfStock', e.target.checked)}
              />
              <Label htmlFor="outOfStock">품절</Label>
            </div>
            <div className="space-y-1">
              <Label>품절 시작일</Label>
              <DatePicker
                value={form.outOfStockFrom}
                disabled={readOnly}
                onChange={(v) => updateField('outOfStockFrom', v)}
              />
            </div>
            <div className="space-y-1">
              <Label>품절 종료일</Label>
              <DatePicker
                value={form.outOfStockTo}
                disabled={readOnly}
                onChange={(v) => updateField('outOfStockTo', v)}
              />
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <Label>수수료 비고</Label>
              <textarea
                rows={3}
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.standardCommissionNote}
                disabled={readOnly}
                onChange={(e) =>
                  updateField('standardCommissionNote', e.target.value)
                }
              />
            </div>
            <div className="space-y-1">
              <Label>인센티브 비고</Label>
              <textarea
                rows={3}
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.incentiveNote}
                disabled={readOnly}
                onChange={(e) => updateField('incentiveNote', e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={saveOpen} onOpenChange={setSaveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>저장 확인</AlertDialogTitle>
            <AlertDialogDescription>
              정산 수수료 정보를 저장하시겠습니까?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction onClick={handleSave}>저장</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
